import { formatFileSize, getFileExtension } from "@/utils/fileHelpers.js";
import { escapeHtml } from "./tableCard.js";

/**
 * The documents Oliver makes, by the type a reader knows each by. A file of any other type
 * keeps its plain download link.
 */
const DOCUMENT_TYPES = [
  {
    kind: "pdf",
    name: "PDF",
    icon: "fa-file-pdf",
    extension: ".pdf",
    mimeType: "application/pdf",
  },
  {
    kind: "word",
    name: "Word",
    icon: "fa-file-word",
    extension: ".docx",
    mimeType:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  },
  {
    kind: "excel",
    name: "Excel",
    icon: "fa-file-excel",
    extension: ".xlsx",
    mimeType:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  },
  {
    kind: "powerpoint",
    name: "PowerPoint",
    icon: "fa-file-powerpoint",
    extension: ".pptx",
    mimeType:
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  },
];

/**
 * The document type of a file, by its MIME type once it is known, else by its name's
 * extension (a reference still resolving, or one that failed, has only its name). Null for
 * any file that is not a document.
 */
export function documentType({ mimeType = "", filename = "" }) {
  if (mimeType) {
    return DOCUMENT_TYPES.find((type) => type.mimeType === mimeType) || null;
  }
  const extension = getFileExtension(filename);
  return DOCUMENT_TYPES.find((type) => type.extension === extension) || null;
}

/** "PDF · 220 KB", or the type alone when the size is unknown. */
export function documentDetails(type, size) {
  return size ? `${type.name} · ${formatFileSize(size)}` : type.name;
}

/** A document Oliver made: its type, name and size, and one click to download it. */
export function fileCardHtml(type, { url, filename, size }) {
  const name = escapeHtml(filename);
  const details = escapeHtml(documentDetails(type, size));
  return (
    `<div class="file-card file-card--${type.kind}">` +
    `<a href="${escapeHtml(url)}" download="${name}" class="file-card__main" ` +
    `title="${name}" aria-label="${escapeHtml(
      `Download ${filename}, ${documentDetails(type, size).replace(
        " · ",
        ", "
      )}`
    )}">` +
    `<span class="file-card__icon"><i class="fas ${type.icon}"></i></span>` +
    `<span class="file-card__text"><span class="file-card__name">${name}</span>` +
    `<span class="file-card__details">${details}</span></span>` +
    `<span class="file-card__download"><i class="fas fa-download"></i>` +
    `<span class="file-card__download-label">Download</span></span>` +
    `</a></div>`
  );
}

/** A document's place while its link is still resolving: the card's shape, so nothing jumps. */
export function fileCardSkeletonHtml(filename) {
  return (
    `<div class="file-card file-card--resolving" aria-busy="true" aria-label="${escapeHtml(
      filename
    )}">` +
    `<span class="file-card__main"><span class="file-card__icon"></span>` +
    `<span class="file-card__text"><span class="file-card__bar file-card__bar--name"></span>` +
    `<span class="file-card__bar file-card__bar--details"></span></span></span></div>`
  );
}

/**
 * A document whose link could not be loaded, and the reader's next step: Retry once, then,
 * if that fails too, ask Oliver to create it again.
 */
export function fileCardFailedHtml(filename, retryId = null) {
  const next = retryId
    ? `<button type="button" class="artifact-retry" data-artifact-retry="${escapeHtml(
        retryId
      )}">Retry</button>`
    : "Ask Oliver to create it again.";
  return (
    `<div class="file-card file-card--failed">` +
    `<span class="file-card__main"><span class="file-card__icon">` +
    `<i class="fas fa-file-circle-exclamation"></i></span>` +
    `<span class="file-card__text"><span class="file-card__name">${escapeHtml(
      filename
    )}</span>` +
    `<span class="file-card__details">Couldn't load this file. ${next}</span></span>` +
    `</span></div>`
  );
}
